import { useCallback } from 'react';

/**
 * Interactive controls inside the app header must keep their own actions
 * (menu, logo → home, notifications, avatar). Only empty header chrome
 * (incl. the safe-area strip under the system status bar) scrolls to top.
 */
const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="link"]',
  '[role="combobox"]',
  '[data-no-scroll-top]',
].join(', ');

/** Matches Tailwind `lg:` — desktop chrome / pointer layouts. */
const DESKTOP_MEDIA = '(min-width: 1024px)';

/**
 * Polyfill "tap the top to jump to the top of the page" for the locked app
 * shell (document never scrolls; only `<main>` does).
 *
 * Native iOS status-bar tap only scrolls `window`/`document`, so it is dead
 * in this portal. On mobile/PWA, tapping the app header's empty area
 * (including the `env(safe-area-inset-top)` band) is the production-ready
 * equivalent on iOS and Android.
 *
 * Desktop is intentionally excluded: browsers do not treat title/header
 * chrome as a scroll-to-top control; users have scrollbar, wheel, and Home.
 * Accidental clicks on empty desktop header chrome must not yank long
 * payroll/attendance views back to the top.
 *
 * @param {React.RefObject<HTMLElement|null>} scrollContainerRef - `<main>` ref
 * @returns {(event: React.MouseEvent|React.PointerEvent) => void}
 */
export function useScrollMainToTop(scrollContainerRef) {
  return useCallback(
    (event) => {
      if (event.defaultPrevented) return;
      // Mobile-only gesture (iOS/Android polyfill). No-op at lg+ viewports.
      if (typeof window !== 'undefined' && window.matchMedia(DESKTOP_MEDIA).matches) {
        return;
      }
      if (event.target instanceof Element && event.target.closest(INTERACTIVE_SELECTOR)) {
        return;
      }

      const el = scrollContainerRef?.current;
      if (!el || el.scrollTop <= 0) return;

      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      el.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    },
    [scrollContainerRef],
  );
}

export default useScrollMainToTop;
